/**
 * IconButton/index.js
 * desc: shared base for all icon components
 * note: renders an interactive <button> (with tooltip + aria-label) when given an onClick handler;
 *       otherwise renders a static, non-interactive <figure> (logos, type/status icons, etc.)
 */
'use strict';

import Tooltip from '../../Tooltip';
import { classNames } from '../../../utilities';


const IconButton = ({ baseClassName, children, className, disabled, label, onClick, readOnly, tooltip, ref, ...rest }) => {
    // an icon is interactive (a real <button>) only when it has a click handler and isn't read-only;
    // without a handler it's a static, non-interactive <figure>
    const interactive = !readOnly && !!onClick;
    const classList = classNames(baseClassName, { disabled:disabled, 'read-only':readOnly }, className);

    // tooltip text: an explicit `tooltip` always wins (pass null to suppress); otherwise the
    // icon defaults to its label (its human name) — for static figures too. Icons with no
    // label still show nothing.
    let tipText = tooltip;
    if (tipText === undefined) {
        tipText = label ?? null;
    }

    // note: the handler is guarded in js (not the native disabled attr) so the button stays
    // hoverable and its tooltip still surfaces while disabled.
    const control = interactive ?
        <button
            type="button"
            ref={ ref }
            aria-label={ label }
            aria-disabled={ disabled || undefined }
            className={ classList }
            onClick={ disabled ? null : onClick }
            { ...rest }
        >
            { children }
        </button>
        :
        <figure ref={ ref } className={ classList } { ...rest }>{ children }</figure>;

    return (
        <Tooltip
            asChild
            arrow={ false }
            contentClassName="info-tooltip small"
            text={ tipText || null }
        >
            { control }
        </Tooltip>
    );
};


export default IconButton;
